import React from "react";
import moment from "moment";
import {
  programsAcademy,
  programPaymentScheduleStudentAcademy,
  programsStudentAcademy,
} from "../../../shared/services";
import { CopyProgramSchedulePaymentStudentType } from "../../../types/dto/scheduleStudent";
import { ProgramType } from "../../../types/dto/program";
import {
  calculateQuoteForSchedules,
  computeCurrentMonthPaymentValidity,
  enrichSchedule,
  getCurrentMonthSchedule,
} from "../utils/paymentQuoteUtils";

import type { PaymentGlobalStatus } from "../../../shared/constants/academyStatusColors";

export type { PaymentGlobalStatus };

export interface EnrichedStudentSchedule
  extends CopyProgramSchedulePaymentStudentType {
  effectiveBalance: number;
  cashDiscountAmount: number;
  isPromptPaymentEligible: boolean;
  promptPrice: number;
}

export interface StudentPaymentSummary {
  totalCourseValue: number;
  totalCourseValuePrompt: number;
  totalPaid: number;
  currentBalance: number;
  promptPaymentDiscount: number;
  globalStatus: PaymentGlobalStatus;
  globalStatusLabel: string;
}

function computeGlobalStatus(
  schedules: CopyProgramSchedulePaymentStudentType[]
): { status: PaymentGlobalStatus; label: string } {
  const active = schedules.filter((s) => s.status === "active");
  if (!active.length) {
    return { status: "sin_cuotas", label: "Sin cuotas asignadas" };
  }
  if (active.some((s) => s.payment_status === "overdue")) {
    return { status: "en_mora", label: "En mora" };
  }
  if (active.some((s) => s.payment_status === "pending_verification")) {
    return { status: "en_verificacion", label: "Verificación de pago" };
  }
  if (active.some((s) => s.payment_status === "partial_payment")) {
    return { status: "pago_parcial", label: "Acuerdo de pago parcial" };
  }
  const hasPending = active.some(
    (s) => s.payment_status === "unpaid" && Number(s.balance) > 0
  );
  if (hasPending) {
    return { status: "al_dia", label: "Con cuotas pendientes" };
  }
  return { status: "al_dia", label: "Al día" };
}

function computeSummary(
  schedules: CopyProgramSchedulePaymentStudentType[],
  programPrice: number
): StudentPaymentSummary {
  const active = schedules.filter((s) => s.status === "active");

  const totalFromSchedules = active.reduce(
    (sum, s) => sum + Number(s.program_payment_schedule_price || 0),
    0
  );
  const totalCourseValue =
    totalFromSchedules > 0 ? totalFromSchedules : Number(programPrice || 0);

  const totalCourseValuePrompt = active.reduce((sum, s) => {
    const prompt = Number(s.program_payment_schedule_prompt_price || 0);
    const price = Number(s.program_payment_schedule_price || 0);
    return sum + (prompt > 0 ? prompt : price);
  }, 0);

  const totalPaid = schedules.reduce(
    (sum, s) => sum + Number(s.amount_paid || 0),
    0
  );

  const currentBalance = active
    .filter(
      (s) =>
        s.payment_status !== "paid" &&
        s.payment_status !== "pending_verification"
    )
    .reduce((sum, s) => sum + Math.max(0, Number(s.balance || 0)), 0);

  const quote = calculateQuoteForSchedules(
    active.filter(
      (s) =>
        s.payment_status !== "paid" &&
        s.payment_status !== "pending_verification"
    )
  );

  const { status, label } = computeGlobalStatus(schedules);

  return {
    totalCourseValue,
    totalCourseValuePrompt:
      totalCourseValuePrompt > 0 ? totalCourseValuePrompt : totalCourseValue,
    totalPaid,
    currentBalance,
    promptPaymentDiscount: quote.cash_discount_amount,
    globalStatus: status,
    globalStatusLabel: label,
  };
}

export default function useStudentPaymentStatus(
  programId: number,
  userId: number,
  refreshKey = 0
) {
  const [loading, setLoading] = React.useState(true);
  const [program, setProgram] = React.useState<ProgramType | null>(null);
  const [schedules, setSchedules] = React.useState<
    CopyProgramSchedulePaymentStudentType[]
  >([]);

  const fetchData = React.useCallback(async () => {
    if (!programId || !userId) return;

    setLoading(true);
    try {
      const programRes = await programsAcademy.get(programId);
      setProgram(programRes);

      const studentsRes = await programsStudentAcademy.find({
        query: { program_id: programId, user_id: userId, $limit: 10 },
      });

      const studentIds = (studentsRes.data || []).map(
        (s: { id: number }) => s.id
      );

      if (!studentIds.length) {
        setSchedules([]);
        return;
      }

      const { data } = await programPaymentScheduleStudentAcademy.find({
        query: {
          $limit: 1000,
          program_student_id: { $in: studentIds },
          $sort: { payment_due_date: 1 },
        },
      });

      setSchedules(data || []);
    } catch (err) {
      console.error("Error cargando estado de pagos:", err);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [programId, userId]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const enrichedSchedules = React.useMemo(
    () => schedules.map(enrichSchedule),
    [schedules]
  );

  const summary = React.useMemo(
    () => computeSummary(schedules, program?.price ?? 0),
    [schedules, program?.price]
  );

  const upcomingSchedules = React.useMemo(() => {
    const today = moment().startOf("day");
    return enrichedSchedules
      .filter(
        (s) =>
          s.status === "active" &&
          s.payment_status !== "paid" &&
          s.payment_status !== "pending_verification" &&
          s.payment_due_date &&
          moment(s.payment_due_date).isSameOrAfter(today, "day")
      )
      .sort(
        (a, b) =>
          moment(a.payment_due_date).valueOf() -
          moment(b.payment_due_date).valueOf()
      )
      .slice(0, 5);
  }, [enrichedSchedules]);

  const overdueSchedules = React.useMemo(
    () =>
      enrichedSchedules.filter(
        (s) => s.status === "active" && s.payment_status === "overdue"
      ),
    [enrichedSchedules]
  );

  const currentMonthSchedule = React.useMemo(
    () => getCurrentMonthSchedule(schedules),
    [schedules]
  );

  const currentMonthValidity = React.useMemo(
    () => computeCurrentMonthPaymentValidity(currentMonthSchedule),
    [currentMonthSchedule]
  );

  const currentMonthScheduleEnriched = React.useMemo(
    () =>
      currentMonthSchedule ? enrichSchedule(currentMonthSchedule) : null,
    [currentMonthSchedule]
  );

  return {
    loading,
    program,
    schedules: enrichedSchedules,
    summary,
    upcomingSchedules,
    overdueSchedules,
    currentMonthSchedule: currentMonthScheduleEnriched,
    currentMonthValidity,
    refresh: fetchData,
  };
}
